import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Hook to handle welcome message state and fallbacks
 * Provides robust welcome message handling with timeouts and fallbacks
 */
const useWelcomeMessage = (chatId, projectId, isConnected) => {
  const queryClient = useQueryClient();
  const [isWaitingForWelcome, setIsWaitingForWelcome] = useState(false);
  const [welcomeTimeout, setWelcomeTimeout] = useState(null);
  const [hasReceivedWelcome, setHasReceivedWelcome] = useState(false);
  const welcomeTimeoutRef = useRef(null);
  const connectionTimeRef = useRef(null);
  
  // Track when WebSocket connects and chat is available
  useEffect(() => {
    if (isConnected && chatId) {
      // Check if chat already has messages before starting welcome timeout
      const chatData = queryClient.getQueryData(['chat', chatId]);
      const messages = chatData?.data?.data?.chat?.messages || chatData?.data?.chat?.messages || [];
      
      // Only start welcome timeout if chat is empty
      if (messages.length === 0) {
        connectionTimeRef.current = Date.now();
        setIsWaitingForWelcome(true);
        setHasReceivedWelcome(false);
        
        // Set timeout for welcome message (15 seconds - reduced from 30)
        welcomeTimeoutRef.current = setTimeout(() => {
          console.warn('⚠️ Welcome message timeout - showing fallback');
          setIsWaitingForWelcome(false);
          setWelcomeTimeout(true);
        }, 15000);
        
        return () => {
          if (welcomeTimeoutRef.current) {
            clearTimeout(welcomeTimeoutRef.current);
            welcomeTimeoutRef.current = null;
          }
        };
      } else {
        // Chat has messages, no need to wait for welcome
        setHasReceivedWelcome(true);
        setIsWaitingForWelcome(false);
        setWelcomeTimeout(false);
      }
    } else {
      setIsWaitingForWelcome(false);
      setWelcomeTimeout(false);
      if (welcomeTimeoutRef.current) {
        clearTimeout(welcomeTimeoutRef.current);
        welcomeTimeoutRef.current = null;
      }
    }
  }, [isConnected, chatId, queryClient]);
  
  // Listen for new messages to detect welcome message
  useEffect(() => {
    if (!chatId || !isWaitingForWelcome) return;
    
    const handleNewMessage = () => {
      // Check if we have any messages in the chat
      const chatData = queryClient.getQueryData(['chat', chatId]);
      const messages = chatData?.data?.data?.chat?.messages || chatData?.data?.chat?.messages || [];
      
      if (messages.length > 0) {
        // Check if any message is a welcome message
        const hasWelcomeMessage = messages.some(msg => 
          msg.role === 'assistant' && 
          (msg.metadata?.type === 'welcome_message' || 
           msg.metadata?.auto_generated === true ||
           msg.content?.toLowerCase().includes('welcome'))
        );
        
        if (hasWelcomeMessage) {
          console.log('✅ Welcome message received');
          setIsWaitingForWelcome(false);
          setHasReceivedWelcome(true);
          if (welcomeTimeoutRef.current) {
            clearTimeout(welcomeTimeoutRef.current);
            welcomeTimeoutRef.current = null;
          }
        }
      }
    };
    
    // Check immediately
    handleNewMessage();
    
    // Set up interval to check for new messages
    const interval = setInterval(handleNewMessage, 1000);
    
    return () => {
      clearInterval(interval);
    };
  }, [chatId, isWaitingForWelcome, queryClient]);
  
  // Reset state when chat changes
  useEffect(() => {
    setIsWaitingForWelcome(false);
    setWelcomeTimeout(false);
    setHasReceivedWelcome(false);
    if (welcomeTimeoutRef.current) {
      clearTimeout(welcomeTimeoutRef.current);
      welcomeTimeoutRef.current = null;
    }
  }, [chatId]);
  
  const clearWelcomeTimeout = useCallback(() => {
    if (welcomeTimeoutRef.current) {
      clearTimeout(welcomeTimeoutRef.current);
      welcomeTimeoutRef.current = null;
    }
    setIsWaitingForWelcome(false);
    setWelcomeTimeout(false);
  }, []);
  
  const retryWelcomeMessage = useCallback(async () => {
    if (!projectId) return;
    
    try {
      console.log('🔄 Retrying welcome message...');
      // Invalidate chat data to trigger refetch
      queryClient.invalidateQueries(['chat', chatId]);
      queryClient.invalidateQueries(['chat-messages', chatId]);
      
      // Reset timeout state
      setWelcomeTimeout(false);
      setIsWaitingForWelcome(true);
      
      // Set new timeout
      welcomeTimeoutRef.current = setTimeout(() => {
        console.warn('⚠️ Welcome message retry timeout');
        setIsWaitingForWelcome(false);
        setWelcomeTimeout(true);
      }, 30000);
      
    } catch (error) {
      console.error('Failed to retry welcome message:', error);
    }
  }, [projectId, chatId, queryClient]);
  
  return {
    isWaitingForWelcome,
    welcomeTimeout,
    hasReceivedWelcome,
    clearWelcomeTimeout,
    retryWelcomeMessage,
    connectionTime: connectionTimeRef.current
  };
};

export default useWelcomeMessage;
